// The page's entry. A live page with no stored token (or a Hep.gg refusal handed back) paints the sign-in screen from
// this small module at once, without the engine, the store worker or the console; the game (main.tsx) loads after a
// sign-in and is prefetched at low priority while the screen shows, with the first scene's models and the content
// bundle. With a token, and in the tools' fixture and dev views modes, the game loads straight away and its boot screen
// shows as before.

import { createRoot, type Root } from 'react-dom/client';
import './signin.css';
import { consumeSsoHash, loadToken, saveToken } from './adapter/net/auth';
import { wantsLive } from './adapter/mode';
import { authOptions, SignInScreen } from './ui/frame/SignInScreen';
import { prefetchWorld } from './firstFiles';
import { TitleStripView } from './ui/frame/TitleStrip';
import { startDesktopFrame } from './ui/desktopShell';

declare global {
  interface Window {
    /** the boot's phase, read by index.html's early telemetry */
    __gcBootPhase?: string;
    /** when the sign-in screen first painted and when the player signed in on it (performance.now), for the load
     *  checks and the telemetry's boot times */
    __gcSignIn?: { paintedAt: number | null; signedAt?: number | null };
  }
}

/** The game's files (its chunks, styles, the store worker and its WebAssembly module), written into this module at
 *  build time by vite.config.ts gameFiles(); the marker is left as it is on the dev server. */
const GAME_FILES = '__GC_GAME_FILES__';

function gameFiles(): string[] {
  if (!GAME_FILES.startsWith('[')) return [];
  try {
    return JSON.parse(GAME_FILES) as string[];
  } catch {
    return [];
  }
}

/** The game's files fetched into the HTTP cache at the lowest priority, so the boot after a sign-in finds them there,
 *  then what its first scene needs (firstFiles.ts). */
function prefetchGame(): void {
  prefetchWorld();
  const base = import.meta.env.BASE_URL;
  const probe = document.createElement('link');
  const linkPrefetch = !!probe.relList?.supports?.('prefetch');
  for (const f of gameFiles()) {
    const href = base + f;
    if (linkPrefetch) {
      const l = document.createElement('link');
      l.rel = 'prefetch';
      // modules and styles load in CORS mode: the prefetch matches so its cache entry is used
      if (/\.(js|css)$/.test(f)) l.crossOrigin = 'anonymous';
      l.href = href;
      document.head.appendChild(l);
    } else {
      void fetch(href, { priority: 'low', credentials: 'same-origin' } as RequestInit).catch(() => undefined);
    }
  }
}

let booting: Promise<void> | null = null;
/** Load and run the game; the sign-in screen (when shown) stays until the game's boot screen is in the page. */
function bootGame(root?: Root, host?: HTMLElement): Promise<void> {
  window.__gcBootPhase = 'load';
  booting ??= import('./main').then(async () => {
    if (!root) return;
    const t0 = performance.now();
    while (!document.querySelector('#root [data-boot-stage]') && performance.now() - t0 < 4000) await new Promise((r) => requestAnimationFrame(r));
    root.unmount();
    host?.remove();
  });
  return booting;
}

function showSignIn(error: string | null): void {
  window.__gcBootPhase = 'signin';
  const sign = (window.__gcSignIn = { paintedAt: null as number | null, signedAt: null as number | null });
  void authOptions();
  const host = document.createElement('div');
  host.id = 'signin';
  document.body.appendChild(host);
  const root = createRoot(host);
  root.render(
    <>
      <TitleStripView />
      <SignInScreen
      build="BETA"
      detail={error}
      onToken={async (token) => {
        sign.signedAt ??= performance.now();
        saveToken(token);
        await bootGame(root, host);
      }}
    />
    </>,
  );
  // the first frame with the screen in it, then the game's files once the page is idle
  const painted = () => {
    if (!host.firstChild) return void requestAnimationFrame(painted);
    requestAnimationFrame(() => {
      sign.paintedAt ??= performance.now();
      const idle = (window as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
      if (idle) idle(prefetchGame, { timeout: 2000 });
      else setTimeout(prefetchGame, 200);
    });
  };
  requestAnimationFrame(painted);
}

// the desktop app's own title bar from the first paint (nothing outside the shell)
startDesktopFrame();
if (!wantsLive()) void bootGame();
else {
  const sso = consumeSsoHash();
  if (sso.token) saveToken(sso.token);
  const dev = import.meta.env.DEV ? new URLSearchParams(location.search).get('token') : null;
  if (dev) saveToken(dev);
  if (loadToken()) void bootGame();
  else showSignIn(sso.error ?? null);
}
